import { useAuth } from "@clerk/react"
import {
  type QueryFunctionContext,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query"

type SessionContext = {
  sessionToken: string
  userId: string
}

type SessionQueryContext<TQueryKey extends QueryKey> =
  QueryFunctionContext<TQueryKey> & SessionContext

type SessionQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  "enabled" | "queryFn"
> & {
  enabled?: boolean
  queryFn: (context: SessionQueryContext<TQueryKey>) => Promise<TQueryFnData>
}

type SessionMutationOptions<TData, TError, TVariables, TContext> = Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  "mutationFn"
> & {
  mutationFn: (variables: TVariables, context: SessionContext) => Promise<TData>
}

async function requireSession(
  getToken: ReturnType<typeof useAuth>["getToken"],
  userId: string | null | undefined,
): Promise<SessionContext> {
  if (!userId) {
    throw new Error("Missing authenticated user.")
  }

  const sessionToken = await getToken()
  if (!sessionToken) {
    throw new Error("Missing session token.")
  }

  return { sessionToken, userId }
}

export function useSessionQuery<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: SessionQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> {
  const { getToken, isLoaded, userId } = useAuth()
  const { enabled = true, queryFn, ...rest } = options

  return useQuery({
    ...rest,
    enabled: enabled && isLoaded && Boolean(userId),
    queryFn: async (context) => {
      const session = await requireSession(getToken, userId)
      return queryFn({
        ...context,
        sessionToken: session.sessionToken,
        userId: session.userId,
      })
    },
  })
}

export function useSessionMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: SessionMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { getToken, userId } = useAuth()
  const { mutationFn, ...rest } = options

  return useMutation({
    ...rest,
    mutationFn: async (variables) => {
      const session = await requireSession(getToken, userId)
      return mutationFn(variables, session)
    },
  })
}
